import moment from 'moment';
import SurveyService from '@/services/survey/'; // eslint-disable-line import/no-cycle
import ErrorHandler from '@/tools/errorHandler';
import { i18n } from '@/boot/i18n';
import { DISTRIBUTION_CHANNEL, QUESTION_TYPE, SURVEY_STATUS } from '@/consts/survey';
import { getItensWeight } from '@/utils/getItemsWeight';
import * as types from './mutationTypes';
import SurveyItem from './surveyItem';
import Trees from './trees'; // eslint-disable-line import/no-cycle
import { validate } from '../validator/validators';
import { MAX_LENGTH, MIN_LENGTH, REQUIRED } from '../validator/methods';
import {
  validateEmptyItems, validateImageItem, validateSurveyLanguage, validateSurveyTeams,
} from './validator';

const clearItem = (subitem, index) => {
  const subitemInner = subitem;
  subitemInner.parent_uuid = null;
  subitemInner.uuid = null;
  subitemInner.survey_uuid = null;
  subitemInner.item_uuid = null;
  subitemInner.id = new Date().getTime() + index;
};

/**
 * If children has_interaction associated, disable parent category too
 * (set has_interaction to category can desassociate)
 * @param {Categories tree} tree
 */
const disableParentCategoriesThatChildrenHasInteraction = (children, parent) => {
  const parentInner = parent;
  children.forEach((child) => {
    if ((child.interaction_count || child.answer_count) && child.parent_uuid && parent) {
      parentInner.interaction_count = 1;
    }
    if (child.children) {
      disableParentCategoriesThatChildrenHasInteraction(child.children, child);
    }
  });
};

const setWeight = (survey) => {
  const items = getItensWeight(survey.items);

  const value = Math.floor(100 / items.length);
  let totalWeight = 0;

  let lastIndex = null;
  items.forEach((el, index) => {
    const question = el;
    question.weight = value;
    lastIndex = index;
    totalWeight += value;
  });
  // Treat odd numbers
  if (lastIndex !== null && totalWeight < 100) {
    const valueOdd = 100 - totalWeight;
    items[lastIndex].weight += valueOdd;
  }
};

export default {
  clearNewState({ dispatch, commit }) {
    dispatch('category/clearCategories', null, { root: true });
    dispatch('category/clearCategoriesTree', null, { root: true });
    dispatch('team/clearTeams', null, { root: true });
    dispatch('team/clearTeamsTree', null, { root: true });
    dispatch('clearSurveyIntegrations');
    commit(types.CLEAR_CATEGORIES);
    commit(types.CLEAR_TEAMS_PERMISSION);
    commit(types.CLEAR_USER_TEAMS);
    commit(types.CLEAR_DELETED_ITEMS);
    commit(types.CLEAR_SELECTED_SURVEY_ITEM_INDEX);
    commit(types.CLEAR_SURVEY);
    dispatch('metadata/clearMetadatas', null, { root: true });
    dispatch('integration/clearIntegrations', null, { root: true });
  },
  async getSurveys({ dispatch, commit }, queryParams) {
    commit(types.SET_QUERY_PARAMS, { params: queryParams });
    await dispatch('getAll');
  },
  async getAll({ rootState, dispatch, state }, queryParams = {}) {
    try {
      const pagination = { ...state.queryParams, ...queryParams };
      const response = await SurveyService.getAll(rootState.organization.organization.uuid, pagination);
      const responseData = response.data || {};
      const data = responseData.data || {};
      dispatch('surveysSuccess', {
        surveys: data?.survey,
        total: data?.total_rows,
      });
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getAllTree({ rootState, dispatch }, pagination) {
    const innerPagination = pagination ?? {};

    innerPagination.format = 'tree';
    const response = await SurveyService.getAll(rootState.organization.organization.uuid, innerPagination);
    if (response.data.data.survey) {
      dispatch('surveysSuccess', { surveys: response.data.data.survey });
    }
  },
  async getAllInfo({ rootState, dispatch }, pagination) {
    const response = await SurveyService.getAllInfo(rootState.organization.organization.uuid, pagination);
    if (response.data.data.survey) {
      dispatch('surveysSuccess', { surveys: response.data.data.survey });
    }
  },
  async getSurveySegment({ rootState, dispatch }, uuid) {
    try {
      return await SurveyService.getSurveySegment(rootState.organization.organization.uuid, uuid);
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
    return null;
  },
  async updateArchive({ rootState, dispatch }, { surveyUUID, status }) {
    try {
      return await SurveyService.updateArchive(rootState.organization.organization.uuid, surveyUUID, status);
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }

    return null;
  },
  async delete({ rootState, dispatch }, surveyUUID) {
    try {
      return await SurveyService.delete(rootState.organization.organization.uuid, surveyUUID);
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }

    return null;
  },
  async getOne({ rootState, dispatch }, surveyUUID) {
    try {
      const response = await SurveyService.getOne(rootState.organization.organization.uuid, surveyUUID);
      const responseData = response.data || {};
      const data = responseData.data || {};
      dispatch('setSurveySuccess', {
        survey: data.survey,
        categoriesUUID: data.categories_uuid,
        teamsUUID: data.teams_uuid,
      });
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async getReport({ rootState, dispatch, commit }, surveyUUID) {
    try {
      const response = await SurveyService.getReport(rootState.organization.organization.uuid, surveyUUID);
      const responseData = response.data || {};
      const data = responseData.data || {};
      commit(types.SET_REPORT, { report: data });
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
    }
  },
  async updateCustomerData({ dispatch }, { token, data }) {
    try {
      await SurveyService.updateCustomerData(token, data);
      dispatch('ui/showToastSuccess', i18n.t('survey.web.customer_data_updated'), { root: true });
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
      throw error;
    }
  },
  getSurveyWeb(context, { token, interactionUUID }) {
    return SurveyService.getSurveyWeb(token, interactionUUID);
  },
  async savePartialResponses(context, { token, survey }) {
    try {
      return await SurveyService.savePartialResponses(token, survey);
    } catch (error) {
      /* Adicionei o comentário para remover o toast de erro nas pesquisas com lógica de salto pois futuramente vão criar uma nova implementação */
      // dispatch("ui/showToastError", ErrorHandler.getErrorMessage(error), { root: true });
      // throw error;
    }
    return null;
  },
  async saveCompleteResponses({ dispatch }, { token, survey }) {
    try {
      return await SurveyService.saveCompleteResponses(token, survey);
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
      throw error;
    }
  },
  surveysSuccess({ commit }, { surveys, total }) {
    commit(types.SET_SURVEYS, { surveys });
    commit(types.SET_QUERY_PARAMS, {
      params: {
        rowsNumber: total || 0,
      },
    });
  },
  setSurveySuccess({ commit }, { survey, categoriesUUID, teamsUUID }) {
    commit(types.SET_SURVEY, { survey });
    commit(types.SET_CATEGORIES, { categories: categoriesUUID });
    commit(types.SET_TEAMS_PERMISSION, { teamsPermission: teamsUUID });
  },
  setSurveys({ commit }) {
    commit(types.SET_SURVEYS, []);
  },
  clearSurveys({ commit }) {
    commit(types.CLEAR_SURVEYS);
  },
  clearSurvey({ commit }) {
    commit(types.CLEAR_SURVEY);
    commit(types.CLEAR_TEAMS_PERMISSION);
    commit(types.CLEAR_CATEGORIES);
  },
  clearListState({ commit }) {
    commit(types.CLEAR_SURVEYS);
    commit(types.CLEAR_QUERY_PARAMS);
  },
  setSelectedSurveyItemIndex({ commit }, selectedIndex) {
    commit(types.SET_SELECTED_SURVEY_ITEM_INDEX, { index: selectedIndex });
  },
  clearSelectedSurveyItemIndex({ commit }) {
    commit(types.CLEAR_SELECTED_SURVEY_ITEM_INDEX);
  },
  clearReport({ commit }) {
    commit(types.CLEAR_REPORT);
  },
  setName({ commit, dispatch }, name) {
    commit(types.SET_NAME, { name });
    dispatch('validateName');
  },
  setSurveyItem({ rootState, state, commit }, type) {
    const item = SurveyItem.createSurveyItem(type, rootState.organization.organization.name);
    commit(types.SET_SURVEY_ITEM, { index: state.selectedSurveyItemIndex, item });
  },
  setSurveyItemQuestion({ commit, state }, { question, language }) {
    commit(types.SET_SURVEY_ITEM_QUESTION, { question, language, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemCommentsEnabled({ commit, state }, value) {
    commit(types.SET_SURVEY_ITEM_COMMENTS_ENABLED, { value, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemDetractorComment({ commit, state }, { question, language }) {
    commit(types.SET_SURVEY_ITEM_DETRACTOR_COMMENT, { question, language, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemPromoterComment({ commit, state }, { question, language }) {
    commit(types.SET_SURVEY_ITEM_PROMOTER_COMMENT, { question, language, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemNeutralComment({ commit, state }, { question, language }) {
    commit(types.SET_SURVEY_ITEM_NEUTRAL_COMMENT, { question, language, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemCommentQuestion({ commit, state }, { question, language }) {
    commit(types.SET_SURVEY_ITEM_COMMENT_QUESTION, { question, language, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemButtonDesign({ commit, state }, design) {
    commit(types.SET_SURVEY_ITEM_BUTTON_DESIGN, { design, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemInvertedScale({ commit, state }, scale) {
    commit(types.SET_SURVEY_ITEM_INVERTED_SCALE, { scale, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemObligatory({ commit, state }, isObligatory) {
    commit(types.SET_SURVEY_ITEM_OBLIGATORY, { isObligatory, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemObligatoryDetractor({ commit, state }, isObligatory) {
    commit(types.SET_SURVEY_ITEM_OBLIGATORY_DETRACTOR, { isObligatory, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemObligatoryNeuter({ commit, state }, isObligatory) {
    commit(types.SET_SURVEY_ITEM_OBLIGATORY_NEUTER, { isObligatory, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemObligatoryPromotor({ commit, state }, isObligatory) {
    commit(types.SET_SURVEY_ITEM_OBLIGATORY_PROMOTOR, { isObligatory, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemColorPattern({ commit, state }, pattern) {
    commit(types.SET_SURVEY_ITEM_COLOR_PATTERN, { pattern, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemCustomColor({ commit, state }, color) {
    commit(types.SET_SURVEY_ITEM_CUSTOM_COLOR, { color, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemScaleType({ commit, state }, type) {
    commit(types.SET_SURVEY_ITEM_SCALE_TYPE, { type, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemMinScale({ commit, state }, scale) {
    commit(types.SET_SURVEY_ITEM_MIN_SCALE, { scale, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemMaxScale({ commit, state }, scale) {
    commit(types.SET_SURVEY_ITEM_MAX_SCALE, { scale, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemButtonSubtitleMin({ commit, state }, { subtitle, language }) {
    commit(types.SET_SURVEY_ITEM_BUTTON_SUBTITLE_MIN, { subtitle, language, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemButtonSubtitleMax({ commit, state }, { subtitle, language }) {
    commit(types.SET_SURVEY_ITEM_BUTTON_SUBTITLE_MAX, { subtitle, language, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemUrl({ commit, state }, url) {
    commit(types.SET_SURVEY_ITEM_URL, { url, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemExtension({ commit, state }, extension) {
    commit(types.SET_SURVEY_ITEM_EXTENSION, { extension, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemAlign({ commit, state }, align) {
    commit(types.SET_SURVEY_ITEM_ALIGN, { align, index: state.selectedSurveyItemIndex });
  },
  addSurveyItemOption({ commit, state }, option) {
    commit(types.ADD_SURVEY_ITEM_OPTION, { option, index: state.selectedSurveyItemIndex });
  },
  removeSurveyItemOption({ commit, state }, optionIndex) {
    commit(types.REMOVE_SURVEY_ITEM_OPTION, { optionIndex, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemOptionLanguage({ commit, state }, { option, language, optionIndex }) {
    commit(types.SET_SURVEY_ITEM_OPTION_LANGUAGE, {
      option,
      optionIndex,
      language,
      index: state.selectedSurveyItemIndex,
    });
  },
  setSurveyItemHorizontalAlign({ commit, state }, align) {
    commit(types.SET_SURVEY_ITEM_HORIZONTAL_ALIGN, { align, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemMultipleAnswers({ commit, state }, multiple) {
    commit(types.SET_SURVEY_ITEM_MULTIPLE_ANSWERS, { multiple, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemOptionOther({ commit, state }, other) {
    commit(types.SET_SURVEY_ITEM_OPTION_OTHER, { other, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemRandomize({ commit, state }, randomize) {
    commit(types.SET_SURVEY_ITEM_RANDOMIZE, { randomize, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemCompositeQuestion({ commit, state }, { question, language }) {
    commit(types.SET_SURVEY_ITEM_COMPOSITE_QUESTION, { question, language, index: state.selectedSurveyItemIndex });
  },
  setSurveyItemCompositeCategoryLanguage({ commit, state }, { question, language, categoryIndex }) {
    commit(types.SET_SURVEY_ITEM_COMPOSITE_CATEGORY_LANGUAGE, {
      question,
      language,
      categoryIndex,
      index: state.selectedSurveyItemIndex,
    });
  },
  setSurveyItemCompositeSubCategoryLanguage(
    { commit, state },
    {
      question, language, subCategoryIndex, categoryIndex,
    },
  ) {
    commit(types.SET_SURVEY_ITEM_COMPOSITE_SUB_CATEGORY_LANGUAGE, {
      question,
      language,
      categoryIndex,
      subCategoryIndex,
      index: state.selectedSurveyItemIndex,
    });
  },
  setPrimaryColor({ commit }, color) {
    commit(types.SET_PRIMARY_COLOR, { color });
  },
  setSecondaryColor({ commit }, color) {
    commit(types.SET_SECONDARY_COLOR, { color });
  },
  setDefaultLanguage({ commit }, language) {
    commit(types.SET_DEFAULT_LANGUAGE, { language });
  },
  setMultiLanguage({ commit }, languages) {
    commit(types.SET_MULTI_LANGUAGE, { languages });
  },
  removeAllSurveyItem({ commit, dispatch, state }) {
    state.survey.items.forEach((item) => {
      if (item.uuid) {
        commit(types.ADD_DELETED_ITEMS, item);
      }
    });
    commit(types.CLEAR_SURVEY_ITEMS);
    if (state.survey.jump_rules_enabled) {
      dispatch('toggleJumpLogic', false);
    }
  },
  setCooldownDistribution({ commit }, cooldown) {
    commit(types.SET_COOLDOWN_DISTRIBUTION, { cooldown });
  },
  setCooldownResponses({ commit }, cooldown) {
    commit(types.SET_COOLDOWN_RESPONSES, { cooldown });
  },
  setSurveyItemCompositeEnableDetractor({ commit, state }, { enable, itemIndex }) {
    commit(types.SET_SURVEY_ITEM_COMPOSITE_ENABLE_DETRACTOR, {
      enable,
      itemIndex,
      index: state.selectedSurveyItemIndex,
    });
  },
  setSurveyItemCompositeEnableDetractorChild({ commit, state }, { enable, parentIndex, itemIndex }) {
    commit(types.SET_SURVEY_ITEM_COMPOSITE_ENABLE_DETRACTOR_CHILDREN, {
      enable,
      parentIndex,
      itemIndex,
      index: state.selectedSurveyItemIndex,
    });
  },
  setSurveyItemCompositeEnableNeutral({ commit, state }, { enable, itemIndex }) {
    commit(types.SET_SURVEY_ITEM_COMPOSITE_ENABLE_NEUTRAL, {
      enable,
      itemIndex,
      index: state.selectedSurveyItemIndex,
    });
  },
  setSurveyItemCompositeEnableNeutralChild({ commit, state }, { enable, parentIndex, itemIndex }) {
    commit(types.SET_SURVEY_ITEM_COMPOSITE_ENABLE_NEUTRAL_CHILDREN, {
      enable,
      parentIndex,
      itemIndex,
      index: state.selectedSurveyItemIndex,
    });
  },
  setSurveyItemCompositeEnablePromoter({ commit, state }, { enable, itemIndex }) {
    commit(types.SET_SURVEY_ITEM_COMPOSITE_ENABLE_PROMOTER, {
      enable,
      itemIndex,
      index: state.selectedSurveyItemIndex,
    });
  },
  setSurveyItemCompositeEnablePromoterChild({ commit, state }, { enable, parentIndex, itemIndex }) {
    commit(types.SET_SURVEY_ITEM_COMPOSITE_ENABLE_PROMOTER_CHILDREN, {
      enable,
      parentIndex,
      itemIndex,
      index: state.selectedSurveyItemIndex,
    });
  },
  setChannel({ commit }, channel) {
    commit(types.CLEAR_SURVEY_ITEMS);
    commit(types.SET_CHANNEL, { channel });
  },
  setWeight({ commit }, { weight, index }) {
    commit(types.SET_WEIGHT, { weight, index });
  },
  duplicateSurveyItem({ commit }, question) {
    const newQuestion = JSON.parse(JSON.stringify(question));
    newQuestion.uuid = null;
    newQuestion.survey_uuid = null;
    newQuestion.item_uuid = null;
    newQuestion.uuid = null;
    newQuestion.id = new Date().getTime();
    newQuestion.responses = 0;
    if (newQuestion.items) {
      newQuestion.items.forEach((subitem, index) => {
        clearItem(subitem, index);
        if (subitem.items) {
          subitem.items.forEach((subsubitem, index1) => {
            clearItem(subsubitem, index1);
          });
        }
      });
    }
    commit(types.ADD_SURVEY_ITEM, { item: newQuestion });
  },
  async initSurvey({
    commit, rootState, state, dispatch,
  }, { surveyUUID, isDuplicated }) {
    commit(types.CLEAR_DELETED_ITEMS);
    if (!surveyUUID) {
      const survey = {
        name: '',
        items: [],
        cooldown_distribution: 0,
        cooldown_responses: 0,
        channel: DISTRIBUTION_CHANNEL.WEB,
        primary_color: '',
        secondary_color: '',
        default_lang: rootState.profile.profile.primary_language,
        lang: [],
        status: SURVEY_STATUS.CREATING,
        teams: [],
        categories: [],
        zendesk_integration_uuid: '',
        jump_rules_enabled: false,
      };
      commit(types.SET_SURVEY, { survey });
      return;
    }

    await dispatch('getOne', surveyUUID);

    const { survey } = state;

    if (!survey.items) {
      survey.items = [];
    }

    // DUPLICATE SURVEY
    if (isDuplicated) {
      // Clear survey
      survey.uuid = null;
      survey.name = i18n.t('survey.new.copy', [survey.name]);
      survey.status = SURVEY_STATUS.CREATING;
      survey.items.forEach((el, index) => {
        const item = el;
        // Clear items
        item.uuid = null;
        item.survey_uuid = null;
        item.item_uuid = null;
        item.id = new Date().getTime() + index;
        item.responses = 0;
        if (item.items) {
          item.items.forEach((subitem, index1) => {
            clearItem(subitem, index1);
            if (subitem.items) {
              subitem.items.forEach((subsubitem, index2) => {
                clearItem(subsubitem, index2);
              });
            }
          });
        }
      });
    }
    commit(types.SET_SURVEY, { survey });
    dispatch('metadata/getAll', null, { root: true });
  },
  async getTeamsData({
    dispatch, rootState, state, commit,
  }, userId) {
    const responseUser = await dispatch('user/getOne', userId, { root: true });
    await dispatch('team/getAllTree', null, { root: true });
    await dispatch('team/getAll', null, { root: true });

    // Always include root team
    if (rootState.team.teams) {
      const rootTeam = rootState.team.teams.find((team) => team.root);
      const allTeams = rootState.team.teams.filter((team) => team.members_uuid.indexOf(userId) >= 0);

      const { teamsPermission } = state;
      if (!teamsPermission.includes(allTeams) && !teamsPermission.includes(rootTeam)) {
        allTeams.forEach((team) => {
          teamsPermission.push(team.uuid);
          commit(types.SET_TEAMS_PERMISSION, { teamsPermission });
        });
        teamsPermission.push(rootTeam.uuid);
        commit(types.SET_TEAMS_PERMISSION, { teamsPermission });
      }
    }

    const userTeams = responseUser.teams;

    commit(types.SET_USER_TEAMS, { userTeams });
  },
  async getCategoriesData({ dispatch, rootState }, surveyUUID) {
    const params = { surveyUUID };
    if (!surveyUUID) {
      params.skip_counter = true;
    }
    await dispatch('category/getAllTree', { ...params }, { root: true });
    await dispatch('category/getAll', { ...params }, { root: true });
    if (rootState.category.categoriesTree) {
      disableParentCategoriesThatChildrenHasInteraction(rootState.category.categoriesTree);
    }
  },
  async create({
    commit, state, dispatch, rootState,
  }, isDraft) {
    const valid = await dispatch('validate');
    if (!valid && !isDraft) {
      return false;
    }

    try {
      const { deletedItems } = state;
      for (let i = 0; i < deletedItems.length; i += 1) {
        await SurveyService.removeSurveyItem( // eslint-disable-line no-await-in-loop
          rootState.organization.organization.uuid,
          state.survey.uuid,
          deletedItems[i].uuid,
        );
      }
      commit(types.CLEAR_DELETED_ITEMS);
    } catch (error) {
      dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
      throw error;
    }

    const survey = { ...state.survey };

    setWeight(survey);

    if (!isDraft) {
      survey.status = SURVEY_STATUS.FINISHED;
    }

    if (!survey.name) {
      survey.name = `${i18n.t('survey.tabs.survey')} ${moment()
        .utcOffset(rootState.profile.profile.primary_time_offset)
        .format('YYYY-MM-DD HH:mm:ss')}`;
    }

    const data = { survey, teams_uuid: state.teamsPermission, categories_uuid: state.categories };

    try {
      const response = await SurveyService.create(rootState.organization.organization.uuid, data);

      if (response.data.data.survey) {
        const newSurvey = response.data.data.survey;
        if (!newSurvey.items) {
          newSurvey.items = [];
        }
        newSurvey.items.forEach((el, index) => {
          const item = el;
          item.id = survey.items[index].id;
        });
        if (isDraft) {
          commit(types.SET_SURVEY_UUID, {
            uuid: newSurvey.uuid,
          });
        } else {
          const teamsPermission = response.data.data.teams_uuid;
          const categories = response.data.data.categories_uuid;
          dispatch('setSurveySuccess', {
            survey: newSurvey,
            categoriesUUID: categories,
            teamsUUID: teamsPermission,
          });
        }
      }
      return true;
    } catch (error) {
      if (error?.response?.status !== 400 || !isDraft) {
        dispatch('ui/showToastError', ErrorHandler.getErrorMessage(error), { root: true });
      }
      throw error;
    }
  },
  toggleTeamPermission({ state, rootState }, team) {
    Trees.toggleTeamPermission(team, state.teamsPermission, rootState.team.teams);
  },
  toggleCategories({ state, rootState, dispatch }, clickedCategory) {
    Trees.toggleCategories(clickedCategory, state.categories, rootState.category.categories);
    dispatch('redoSurveyItemCompositeMetrics', clickedCategory);
    dispatch('redoSurveyItemCompositeMetrics');
  },
  toogleAllCategories({ rootState, state, dispatch }, isCheckedAll) {
    if (isCheckedAll) {
      // Remove all categories
      state.categories.forEach((catUUID) => {
        Trees.removeAllCategories(catUUID, rootState.category.categoriesTree);
      });

      return;
    }
    // Include all categories
    rootState.category.categories.forEach((cat) => Trees.addCategory(cat.uuid));
    dispatch('redoSurveyItemCompositeMetrics');
  },
  async insertNewCategory({ state, dispatch }, category) {
    await dispatch('getCategoriesData', state.survey.uuid);
    dispatch('toggleCategories', category);
  },
  addSurveyItem({ rootState, commit }, type) {
    const item = SurveyItem.createSurveyItem(type, rootState.organization.organization.name);
    commit(types.ADD_SURVEY_ITEM, { item });
  },
  removeCategorieItem({ commit }, item) {
    commit(types.REMOVE_CATEGORIE_ITEM, item);
  },
  removeSurveyItem({ state, commit }, index) {
    if (state.survey.items[index].uuid) {
      commit(types.ADD_DELETED_ITEMS, state.survey.items[index]);
    }
    commit(types.REMOVE_SURVEY_ITEM, index);
  },
  getPrincipalItem({ state }) {
    let index = 0;
    let foundPrincipal = null;

    while (foundPrincipal === null && index < state.survey.items.length) {
      const item = state.survey.items[index];
      if (item.type !== QUESTION_TYPE.IMAGE && item.type !== QUESTION_TYPE.LABEL) {
        foundPrincipal = index;
      }
      index += 1;
    }
    return foundPrincipal;
  },
  async validate({ dispatch, state, rootState }) {
    await dispatch('validator/removeError', 'surveyItem', { root: true });
    const validateLanguage = validateSurveyLanguage(state.survey);
    if (validateLanguage) {
      await dispatch('validator/addError', validateLanguage, { root: true });
    }
    const validateImages = validateImageItem(state.survey);
    if (validateImages) {
      await dispatch('validator/addError', validateImages, { root: true });
    }
    const validateEmpty = validateEmptyItems(state.survey);
    if (validateEmpty) {
      await dispatch('validator/addError', validateEmpty, { root: true });
    }
    const { userTeams } = state;
    const userTeamsList = userTeams.map((t) => t.uuid);
    const validateTeams = validateSurveyTeams(state.teamsPermission, userTeamsList);
    if (validateTeams) {
      await dispatch('validator/addError', validateTeams, { root: true });
    } else {
      await dispatch('validator/removeError', 'teams', { root: true });
    }
    return rootState.validator.errors.length === 0;
  },
  async validateName({ dispatch, state }) {
    await dispatch('validator/removeError', 'name', { root: true });
    if (!validate(REQUIRED, state.survey.name)) {
      await dispatch(
        'validator/addError',
        {
          field: 'name',
          message: i18n.t('general.rules.required'),
        },
        { root: true },
      );
    }
    if (!validate(MIN_LENGTH, state.survey.name, 4)) {
      await dispatch(
        'validator/addError',
        {
          field: 'name',
          message: `${i18n.t('general.rules.min_length')} ${4}`,
        },
        { root: true },
      );
    }
    if (!validate(MAX_LENGTH, state.survey.name, 56)) {
      await dispatch(
        'validator/addError',
        {
          field: 'name',
          message: `${i18n.t('general.rules.max_length')} ${56}`,
        },
        { root: true },
      );
    }
  },
};
